import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, completeStudy, loadStudy, login, savePiece, savePieceOnExit } from "./api";
import { MidiPlayer } from "./components/MidiPlayer";
import type { Candidate, StudyData, StudyPiece } from "./types";

type Screen = "loading" | "login" | "intro" | "study" | "complete";

function pieceComplete(piece: StudyPiece, minimumMs: number): boolean {
  return piece.candidates.every((candidate) =>
    candidate.listenedMs >= minimumMs && candidate.score !== null,
  );
}

function LoginScreen({ onLogin, busy, error }: { onLogin: (nickname: string, password: string) => void; busy: boolean; error: string }) {
  const [nickname, setNickname] = useState("");
  const [password, setPassword] = useState("");
  return (
    <main className="centered-shell">
      <section className="login-card">
        <div className="eyebrow">MIREX 2026 · Internal evaluation</div>
        <h1>Piano continuation<br />listening study</h1>
        <p className="lead">Choose a recognizable nickname so you can resume your progress later, then enter the shared study password.</p>
        <form onSubmit={(event) => { event.preventDefault(); onLogin(nickname, password); }}>
          <label htmlFor="nickname">Nickname</label>
          <input
            id="nickname"
            value={nickname}
            onChange={(event) => setNickname(event.target.value)}
            placeholder="Your nickname"
            autoComplete="username"
            maxLength={32}
          />
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Study password"
            autoComplete="current-password"
          />
          {error && <p className="form-error" role="alert">{error}</p>}
          <button className="primary-button" type="submit" disabled={busy || !nickname.trim() || !password}>
            {busy ? "Checking…" : "Continue"}
          </button>
        </form>
      </section>
    </main>
  );
}

function IntroScreen({ onBegin }: { onBegin: () => void }) {
  return (
    <main className="centered-shell">
      <section className="intro-card">
        <div className="eyebrow">Before you begin</div>
        <h1>Listen carefully, judge independently.</h1>
        <div className="intro-grid">
          <div><span>01</span><p>There are 10 pieces and five anonymous continuations per piece.</p></div>
          <div><span>02</span><p>Use headphones in a quiet place. Expect approximately 20–30 minutes.</p></div>
          <div><span>03</span><p>Listen to the prompt first, then at least 5 seconds of every candidate.</p></div>
          <div><span>04</span><p>After listening, place all five candidates on one shared 0–10 preference axis.</p></div>
        </div>
        <p className="privacy-note">Your progress is saved automatically. You can return with the same nickname before final submission.</p>
        <button className="primary-button" type="button" onClick={onBegin}>Begin study</button>
      </section>
    </main>
  );
}

function CandidateCard({
  candidate,
  minimumMs,
  promptReady,
  onListened,
}: {
  candidate: Candidate;
  minimumMs: number;
  promptReady: boolean;
  onListened: (milliseconds: number) => void;
}) {
  const listened = candidate.listenedMs >= minimumMs;
  return (
    <article className={`candidate-card ${listened ? "listened" : ""}`}>
      <header>
        <div className="candidate-label">Candidate {candidate.label}</div>
        <span className={`candidate-status ${listened ? "ready" : ""}`}>{listened ? "Ready to place" : "Listen 5s"}</span>
      </header>
      <MidiPlayer
        src={candidate.midiUrl}
        durationMs={candidate.durationMs}
        listenedMs={candidate.listenedMs}
        minimumMs={minimumMs}
        disabled={!promptReady}
        onListened={onListened}
      />
      {!promptReady && <p className="candidate-hint">Finish the shared prompt before listening.</p>}
    </article>
  );
}

function PreferenceAxis({
  candidates,
  minimumMs,
  onChange,
}: {
  candidates: Candidate[];
  minimumMs: number;
  onChange: (candidateId: string, score: number) => void;
}) {
  return (
    <section className="preference-panel">
      <header>
        <div>
          <div className="eyebrow">Overall preference</div>
          <h2>Place every candidate on the same 0–10 axis.</h2>
        </div>
        <p>0 = unusable · 5 = acceptable · 10 = exceptional. Ties are allowed.</p>
      </header>
      <div className="preference-axis">
        {candidates.map((candidate) => {
          const enabled = candidate.listenedMs >= minimumMs;
          return (
            <label className={`axis-row ${candidate.score === null ? "unplaced" : ""} ${enabled ? "" : "locked"}`} key={candidate.id}>
              <strong>{candidate.label}</strong>
              <input
                type="range"
                min="0"
                max="10"
                step="0.1"
                value={candidate.score ?? 5}
                disabled={!enabled}
                aria-label={`Candidate ${candidate.label} preference`}
                onInput={(event) => onChange(candidate.id, Number(event.currentTarget.value))}
              />
              <output>{enabled ? (candidate.score === null ? "Drag" : candidate.score.toFixed(1)) : "Listen 5s"}</output>
            </label>
          );
        })}
        <div className="axis-ticks" aria-hidden="true">
          {Array.from({ length: 11 }, (_, value) => <span key={value}>{value}</span>)}
        </div>
      </div>
    </section>
  );
}

function StudyScreen({
  data,
  setData,
  onComplete,
}: {
  data: StudyData;
  setData: React.Dispatch<React.SetStateAction<StudyData | null>>;
  onComplete: () => void;
}) {
  const [index, setIndex] = useState(() => {
    const firstIncomplete = data.pieces.findIndex((piece) => !pieceComplete(piece, data.minimumListenedMs));
    return firstIncomplete < 0 ? data.pieces.length - 1 : firstIncomplete;
  });
  const [promptReady, setPromptReady] = useState<Record<string, boolean>>(() => Object.fromEntries(
    data.pieces.map((piece) => [piece.id, piece.candidates.some((candidate) => candidate.listenedMs > 0)]),
  ));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const saveTimer = useRef<number | null>(null);
  const piece = data.pieces[index];
  const latestPiece = useRef(piece);
  latestPiece.current = piece;

  const updateCandidateListened = useCallback((candidateId: string, milliseconds: number) => {
    setData((current) => current ? {
      ...current,
      pieces: current.pieces.map((currentPiece) => currentPiece.id !== piece.id ? currentPiece : {
        ...currentPiece,
        candidates: currentPiece.candidates.map((candidate) => candidate.id !== candidateId ? candidate : {
          ...candidate,
          listenedMs: Math.max(candidate.listenedMs, milliseconds),
        }),
      }),
    } : current);
  }, [piece.id, setData]);

  const updateCandidateScore = useCallback((candidateId: string, score: number) => {
    setData((current) => current ? {
      ...current,
      pieces: current.pieces.map((currentPiece) => currentPiece.id !== piece.id ? currentPiece : {
        ...currentPiece,
        candidates: currentPiece.candidates.map((candidate) =>
          candidate.id === candidateId ? { ...candidate, score } : candidate),
      }),
    } : current);
  }, [piece.id, setData]);

  const persist = useCallback(async (target: StudyPiece) => {
    setSaving(true);
    setError("");
    try {
      await savePiece(target);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Could not save progress");
      throw requestError;
    } finally {
      setSaving(false);
    }
  }, []);

  useEffect(() => {
    if (saveTimer.current === null) {
      saveTimer.current = window.setTimeout(() => {
        saveTimer.current = null;
        void persist(latestPiece.current).catch(() => undefined);
      }, 1_000);
    }
  }, [piece, persist]);

  useEffect(() => {
    const flush = () => savePieceOnExit(latestPiece.current);
    const flushWhenHidden = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", flushWhenHidden);
    return () => {
      if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", flushWhenHidden);
    };
  }, []);

  const navigate = async (nextIndex: number) => {
    if (saveTimer.current !== null) {
      window.clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    try {
      await persist(piece);
      setIndex(nextIndex);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      // The inline error keeps the evaluator on this page.
    }
  };

  const finish = async () => {
    if (saveTimer.current !== null) {
      window.clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    try {
      await persist(piece);
      await completeStudy();
      onComplete();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Could not submit study");
    }
  };

  const complete = pieceComplete(piece, data.minimumListenedMs);
  return (
    <main className="study-shell">
      <nav className="topbar">
        <div>
          <span className="mark">M</span>
          <span>MIREX internal listening study</span>
        </div>
        <div className="progress-copy">Piece {index + 1} of {data.pieces.length} · {saving ? "Saving…" : "Saved"}</div>
      </nav>
      <div className="progress-bar"><span style={{ width: `${((index + 1) / data.pieces.length) * 100}%` }} /></div>
      <section className="study-content">
        <header className="piece-heading">
          <div className="eyebrow">Piece {index + 1}</div>
          <h1>Listen to the shared prompt.</h1>
          <p>All five candidates continue the same music. Their identities and order are hidden.</p>
        </header>
        <section className={`prompt-card ${promptReady[piece.id] ? "ready" : ""}`}>
          <div>
            <span>Shared prompt</span>
            <strong>{promptReady[piece.id] ? "Completed" : "Play to the end"}</strong>
          </div>
          <MidiPlayer key={piece.id} src={piece.promptMidiUrl} durationMs={piece.promptDurationMs} compact onEnded={() => setPromptReady((current) => ({ ...current, [piece.id]: true }))} />
        </section>
        <div className="candidate-list">
          {piece.candidates.map((candidate) => (
            <CandidateCard
              key={candidate.id}
              candidate={candidate}
              minimumMs={data.minimumListenedMs}
              promptReady={Boolean(promptReady[piece.id])}
              onListened={(milliseconds) => updateCandidateListened(candidate.id, milliseconds)}
            />
          ))}
        </div>
        <PreferenceAxis
          candidates={piece.candidates}
          minimumMs={data.minimumListenedMs}
          onChange={updateCandidateScore}
        />
        {error && <p className="save-error" role="alert">{error}</p>}
        <footer className="study-navigation">
          <button className="secondary-button" type="button" disabled={index === 0 || saving} onClick={() => void navigate(index - 1)}>Previous</button>
          <span>{complete ? "Page complete" : "Listen to each candidate, then place all five on the axis"}</span>
          {index < data.pieces.length - 1 ? (
            <button className="primary-button" type="button" disabled={!complete || saving} onClick={() => void navigate(index + 1)}>Next piece</button>
          ) : (
            <button className="primary-button" type="button" disabled={!complete || saving} onClick={() => void finish()}>Submit study</button>
          )}
        </footer>
      </section>
    </main>
  );
}

function CompleteScreen() {
  return (
    <main className="centered-shell">
      <section className="complete-card">
        <div className="completion-mark">✓</div>
        <div className="eyebrow">Submission received</div>
        <h1>Thank you for listening.</h1>
        <p className="lead">Your responses are locked. Global results and candidate identities will remain hidden until data collection ends.</p>
        <p>Your ten preference placements were saved successfully.</p>
      </section>
    </main>
  );
}

export default function App() {
  const [screen, setScreen] = useState<Screen>("loading");
  const [data, setData] = useState<StudyData | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async (afterLogin = false) => {
    try {
      const study = await loadStudy();
      setData(study);
      setScreen(study.completed ? "complete" : afterLogin ? "intro" : "study");
    } catch (requestError) {
      if (requestError instanceof ApiError && requestError.status === 401) setScreen("login");
      else {
        setError(requestError instanceof Error ? requestError.message : "Could not load study");
        setScreen("login");
      }
    }
  }, []);

  useEffect(() => { void refresh(false); }, [refresh]);

  const submitLogin = async (nickname: string, password: string) => {
    setBusy(true);
    setError("");
    try {
      await login(nickname, password);
      await refresh(true);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Could not sign in");
    } finally {
      setBusy(false);
    }
  };

  if (screen === "loading") return <main className="loading-screen"><span className="loading-note">♪</span><p>Preparing the listening study…</p></main>;
  if (screen === "login") return <LoginScreen onLogin={(nickname, password) => void submitLogin(nickname, password)} busy={busy} error={error} />;
  if (!data) return null;
  if (screen === "intro") return <IntroScreen onBegin={() => setScreen("study")} />;
  if (screen === "complete") return <CompleteScreen />;
  return <StudyScreen data={data} setData={setData} onComplete={() => setScreen("complete")} />;
}
